import { Builder } from '@builder.io/react';

import { BUILDER_MODELS } from '~/builder/constants/models';
import { useBuilderContent } from '~/builder/utils/useBuilderContent';
import useBuilderJson from '~/builder/utils/useBuilderJson';

/**
 * Custom hook to retrieve category navigation data using Builder.
 *
 * This hook fetches content for the category navigation model
 * from Builder. It determines if the content is loaded and provides
 * the necessary data for rendering.
 *
 * @param {Object} [options={}] - Options for the hook.
 * @param {Object} [options.builderProperties={}] - Additional properties to override the
 *  default Builder properties by a consumer.
 *
 * @returns {Object} An object containing:
 * - `builderJson` {Object} - The raw JSON data fetched from Builder
 * - `isLoading` {boolean} - A flag indicating whether the content is still loading
 * - `content` {Object} - The processed content data
 * - `locale` {string} - The locale of the content
 */
function useBuilderCategoryNav({ builderProperties = {} } = {}) {
  const defaultBuilderProperties = {
    modelName: BUILDER_MODELS.CATEGORY_NAVIGATION,
    ...builderProperties,
  };
  const { builderJson, isLoading } = useBuilderJson(defaultBuilderProperties);
  const { content, locale } = useBuilderContent(builderJson);

  const builderContentLoaded =
    (!isLoading && content) || Builder.isPreviewing || Builder.isEditing;

  return { builderJson, isLoading: !builderContentLoaded, content, locale };
}

export default useBuilderCategoryNav;
